import { Flex, Text, Badge, Box, Button } from '@chakra-ui/react';
import PlanCardFeatures from './PlanCardFeatures';
import {
  PLAN_CARD_PRICE_ROW_MIN_H,
  PLAN_CARD_PRICE_BLOCK_MIN_H,
  PLAN_CARD_GRADIENT_BORDER,
  PLAN_CARD_BORDER,
} from './planCardLayout';

const CUSTOM_MIN_MONTHLY = '£200';
const CUSTOM_MIN_ANNUAL_MONTHLY = '£168';

const STATIC_PLAN_FEATURES = {
  custom: {
    basePlan: 'Pilot',
    bullets: [
      'Flexible pass volumes',
      'Multiple templates and setups',
      'Flexible feature access',
      'Tailored onboarding support',
    ],
  },
  enterprise: {
    basePlan: 'Custom',
    bullets: [
      'Custom contracts and invoicing',
      'Advanced security and custom SLAs',
      'Priority enterprise support',
      'White labelling',
    ],
  },
};

const StaticSubscriptionOption = ({
  id,
  label,
  buttonText,
  onRequestCustomPlan,
  handleBookDemo,
  customPlanRequest,
  interval,
}) => {
  const isCustomDisabled = id === 'custom' && customPlanRequest;
  const isCustom = id === 'custom';
  const { basePlan, bullets } = STATIC_PLAN_FEATURES[id] ?? {
    basePlan: null,
    bullets: [],
  };
  const featuresHeading = basePlan && (
    <>
      All of{' '}
      <Text as="span" color="orangePrimary">
        {basePlan}
      </Text>
      , plus
    </>
  );

  return (
    <Flex
      direction="column"
      alignItems="center"
      w="100%"
      h="full"
      borderRadius="15px"
      p={8}
      position="relative"
      opacity={isCustomDisabled ? 0.7 : 1}
      _hover={
        isCustomDisabled ? undefined : { bg: 'rgba(255, 255, 255, 0.05)' }
      }
      {...(isCustom
        ? { _before: PLAN_CARD_GRADIENT_BORDER }
        : PLAN_CARD_BORDER)}
      bg={'transparent'}
      boxShadow={isCustom ? '5px 10px 10px 0px rgba(0, 0, 0, 0.1)' : 'none'}
    >
      {id === 'custom' && (
        <Badge
          position="absolute"
          top="0"
          transform="translateY(-40%)"
          color="white"
          bg="linear-gradient(180deg, #FF9554 16%, #FF4935 100%)"
          fontSize="sm"
          px={3}
          py={1.5}
          borderRadius="full"
        >
          Recommended
        </Badge>
      )}

      <Flex
        direction="column"
        gap={3}
        alignItems="flex-start"
        w="full"
        h="full"
        borderRadius="12px"
      >
        <Flex direction="column" gap={1} w="full">
          <Text textStyle="heading4">{label}</Text>
          <Flex
            direction="column"
            alignItems="flex-start"
            gap={0.5}
            w="full"
            minH={PLAN_CARD_PRICE_BLOCK_MIN_H}
          >
            <Flex
              direction="row"
              alignItems="baseline"
              minH={PLAN_CARD_PRICE_ROW_MIN_H}
            >
              {isCustom ? (
                <Text textStyle="heading5">
                  from{' '}
                  <Text as="span" textStyle="heading2">
                    {interval === 'year'
                      ? CUSTOM_MIN_ANNUAL_MONTHLY
                      : CUSTOM_MIN_MONTHLY}
                  </Text>
                  <Text as="span" color="textGrey" fontWeight="medium">
                    /mo
                  </Text>
                </Text>
              ) : (
                <>
                  <Box
                    as="span"
                    textStyle="heading2"
                    w={0}
                    overflow="hidden"
                    aria-hidden="true"
                  >
                    &nbsp;
                  </Box>
                  <Text textStyle="heading3">Let's talk</Text>
                </>
              )}
            </Flex>
            <Text textStyle="bodySmall" color="#465467">
              {isCustom
                ? `Billed ${interval === 'year' ? 'annually' : 'monthly'}`
                : 'Pricing based on your needs'}
            </Text>
          </Flex>
        </Flex>

        {bullets.length > 0 && (
          <PlanCardFeatures heading={featuresHeading} features={bullets} />
        )}
        <Box marginTop="auto" w="full">
          <Button
            variant={isCustom ? 'gradient' : 'secondary'}
            size="sm"
            w="full"
            isDisabled={isCustomDisabled}
            onClick={() => {
              if (isCustomDisabled) return;
              if (id === 'custom') {
                onRequestCustomPlan?.(interval);
              } else if (id === 'enterprise') {
                handleBookDemo();
              }
            }}
          >
            {isCustomDisabled ? 'Request in progress' : buttonText}
          </Button>
        </Box>
      </Flex>
    </Flex>
  );
};

export default StaticSubscriptionOption;
